/**
 * Standort-Filter: die Abwahl des Nutzers ueberlebt den Seitenwechsel.
 *
 * Wer eine PLZ im Konto hinterlegt hat, startet die Eventliste mit Umkreis um zuhause. Das ist
 * gewollt — aber wer den Standort danach bewusst entfernt (weil er ein weit entferntes Event
 * sucht), bekam ihn bei jeder Rueckkehr aus der Detailseite wieder: Landing wird dabei neu
 * aufgebaut, und die Vorbefuellung las die PLZ erneut aus dem Profil.
 *
 * Deshalb wird die Abwahl festgehalten — in sessionStorage, nicht in localStorage: sie gehoert
 * zur laufenden Suche, ein neuer Besuch soll wieder mit dem Umkreis um zuhause anfangen.
 */
const KEY = 'radevents.locationOff';

/** True, wenn der Nutzer den Standort-Filter in dieser Sitzung weggenommen hat. */
export const isLocationOff = (): boolean => {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    // Privater Modus / gesperrter Speicher: dann eben ohne Gedaechtnis, aber nicht mit Absturz.
    return false;
  }
};

/** Merkt: Standort bewusst entfernt (Chip, „zuruecksetzen", Umkreis aus, Filter zuruecksetzen). */
export const markLocationOff = (): void => {
  try { sessionStorage.setItem(KEY, '1'); } catch { /* s. o. */ }
};

/** Merkt: Standort wieder gewollt (PLZ getippt, Ort gewaehlt, Umkreis auf der Karte gesetzt). */
export const markLocationOn = (): void => {
  try { sessionStorage.removeItem(KEY); } catch { /* s. o. */ }
};

/**
 * Welche PLZ soll aus dem Profil in den Filter vorbelegt werden — oder keine?
 *
 * @param profileLocation Ortsangabe aus dem Konto (Freitext, enthaelt meist die PLZ)
 * @param currentPlz      PLZ, die im Filter schon steht
 * @param off             Ergebnis von {@link isLocationOff}
 * @returns die vorzubelegende PLZ oder null, wenn nichts vorzubelegen ist
 */
export const plzToPrefill = (
  profileLocation: string | undefined | null,
  currentPlz: string,
  off: boolean,
): string | null => {
  // Bereits gesetzte PLZ nie ueberschreiben: der Nutzer tippt sonst gegen sein eigenes Profil an.
  if (off || currentPlz || !profileLocation) return null;
  return profileLocation.match(/\d{5}/)?.[0] ?? null;
};

/**
 * Ort aus dem Konto zur PLZ: „30657 Hannover“ → „Hannover“. Passt die PLZ nicht zum Konto oder
 * steht kein Ort dahinter, bleibt es bei der PLZ.
 */
export const ortZurPlz = (profileLocation: string | undefined | null, plz: string): string => {
  const m = profileLocation?.match(/(\d{4,5})\s+([^,\d][^,]*)/);
  return m && m[1] === plz ? m[2].trim() : plz;
};

const ORT_KEYS = ['radevents.userPlz', 'radevents.userLat', 'radevents.userLng'] as const;

/**
 * Eine im Ortsfeld getippte PLZ samt Koordinaten. Angemeldet nur für die Sitzung, damit sie die
 * PLZ im Profil nicht ersetzt; ohne Konto dauerhaft, weil es dort kein Profil gibt.
 */
export const ortMerken = (angemeldet: boolean, plz: string, lat: number, lng: number): void => {
  try {
    const speicher = angemeldet ? sessionStorage : localStorage;
    speicher.setItem(ORT_KEYS[0], plz);
    speicher.setItem(ORT_KEYS[1], String(lat));
    speicher.setItem(ORT_KEYS[2], String(lng));
  } catch { /* s. o. */ }
};

/** Gemerkter Wert: zuerst der aus dieser Sitzung, dann der dauerhafte. */
export const ortGemerkt = (key: typeof ORT_KEYS[number]): string | null => {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** Getippte PLZ vergessen, in beiden Speichern. */
export const ortVergessen = (): void => {
  try {
    ORT_KEYS.forEach(k => { sessionStorage.removeItem(k); localStorage.removeItem(k); });
  } catch { /* s. o. */ }
};
